import {
  GetServerSideProps,
  GetServerSidePropsContext,
  GetServerSidePropsResult
} from 'next';
import { ParsedUrlQuery } from 'querystring';
import React, { FC } from 'react';
import { Category } from 'src/api/types';
import Head from 'next/head';
import {
  isNoIndexing,
  getProductsListByLetterPageMetaData
} from 'src/utils/seo';
import { useRouter } from 'next/router';
import {
  CustomErrorProps,
  triggerCustomStatusCodeRedirect,
  useHandleCustomErrorRedirect
} from 'src/ssr/errorStatusCodeRedirect';
import { fetchCache } from 'src/api/caching/cacheUtils';
import {
  generalSSRProps,
  getCacheConfig,
  setSSRCacheControl
} from '../../ssr/generalSsrUtil';
import postProductsByLetter, {
  PostProductsByLetterRequest
} from '../../api/requests/postProductsByLetter';
import { extractPageNumber } from '../../utils/links';
import getLettersList, {
  GetLettersResponse
} from '../../api/requests/getLetters';
import DrugsByLetterContainer from '../../page-components/letter/DrugsByLetterContainer';

const defaultPageSize = 120;

interface Props extends CustomErrorProps {
  drugListResponse: PostProductsByLetterRequest;
  lettersListResponse: GetLettersResponse[];
  pageNumber: number;
  categoriesList: Category[];
}

const ProductsByLetterPage: FC<Props> = ({
  drugListResponse,
  lettersListResponse,
  pageNumber,
  hasError,
  statusCode
}) => {
  const router = useRouter();
  const { redirectToStatusCodePage } = useHandleCustomErrorRedirect({
    hasError,
    statusCode
  });
  const { query, locale } = router;
  const { params } = query;
  const [letter] = params as string[];

  if (hasError) {
    return redirectToStatusCodePage();
  }

  const seoData = getProductsListByLetterPageMetaData(letter, locale);

  return (
    <>
      <Head>
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <meta
          name="robots"
          content={isNoIndexing ? 'noindex' : 'noindex, follow'}
          key="robots"
        />
      </Head>
      <DrugsByLetterContainer
        drugListResponse={drugListResponse}
        lettersListResponse={lettersListResponse}
        pageNumber={pageNumber}
      />
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext<ParsedUrlQuery>
) => {
  setSSRCacheControl(context);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return fetchCache<GetServerSidePropsResult<{ [key: string]: any }>>(
    getCacheConfig(context),
    async () => {
      const {
        res,
        req: { cookies },
        query
      } = context;
      const generalProps = await generalSSRProps(context);
      const { params } = query;
      const [letter, pageLink] = params as string[];

      const pageNumber = extractPageNumber(pageLink);

      const drugList = await postProductsByLetter(
        {
          pageSize: defaultPageSize,
          pageNumber,
          letter: letter as string
        },
        {
          cookies,
          ip: generalProps.ip,
          locale: context?.locale,
          useCache: true
        }
      );

      const lettersListResponse = await getLettersList({
        cookies,
        ip: generalProps.ip,
        locale: context?.locale,
        useCache: true
      });

      const foundLetter = lettersListResponse?.find(
        (item) => item.translitName === letter
      );

      if (!foundLetter) {
        // Redirect to 410 if letter not found
        return triggerCustomStatusCodeRedirect(res, 410);
      }

      return {
        props: {
          ...generalProps,
          drugListResponse: drugList,
          lettersListResponse,
          pageNumber
        }
      };
    }
  );
};

export default ProductsByLetterPage;
